import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { DataTable } from '@/components/ui/table/data-table';
import useMembers from '@/hooks/useMembers';

import { createColumns } from './columns';
import { type McpServer } from './hooks/useMcpServers';

interface McpServersTableProps {
  mcpServers: McpServer[];
  isLoading?: boolean;
  className?: string;
  canWrite?: boolean;
  activeRowId?: string;
  onToggleActive: (server: McpServer, isActive: boolean) => void;
  onDelete: (server: McpServer) => void;
  onResync: (server: McpServer) => void;
  onEdit: (server: McpServer) => void;
  /** Row click — opens the server's detail page. Edit stays in the row kebab. */
  onSelect: (server: McpServer) => void;
}

export function McpServersTable({
  mcpServers,
  isLoading = false,
  className,
  canWrite = true,
  activeRowId,
  onToggleActive,
  onDelete,
  onResync,
  onEdit,
  onSelect,
}: McpServersTableProps) {
  const { t } = useTranslation();
  const { getMember } = useMembers();

  const columns = useMemo(
    () => createColumns({ onToggleActive, onDelete, onResync, onEdit, getMember, canWrite }),
    [onToggleActive, onDelete, onResync, onEdit, getMember, canWrite]
  );

  return (
    <DataTable.Root
      fill
      columns={columns}
      data={mcpServers}
      rowCount={mcpServers.length}
      isLoading={isLoading}
      resourceName="mcp"
      enableRowHover
      className={className}
      emptyStateMessage={t('mcp.no_servers')}
      getRowId={(row) => row.mcp_id}
      activeRowId={activeRowId}
      onRowClick={onSelect}
    >
      <DataTable.Content />
      <DataTable.Pagination />
    </DataTable.Root>
  );
}
